import { size, first, includes, each, merge, isNil, isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { hasFeature } from 'src/common/utils/organizationHasFeature';

import { IConnectSearchPage, ICreatorSearchOptions } from '../models';
import { mapSearchFiltersToAPIParams } from '../utils/searchParams';

export const showCreateFeaturesSelector = createSelector(
  (state: IConnectSearchPage) => state.external.organization,
  (organization) => !isEmpty(organization),
);

export const imageSearchEnabledSelector = createSelector(
  (state: IConnectSearchPage) => state.external.organization,
  (state: IConnectSearchPage) => state.external.clientId,
  (organization, clientId) => {
    return (
      !isNil(clientId) || (
        organization && (organization.is_beta_tester || hasFeature(organization, 'image_search'))
      )
    );
  },
);

export const showCreatorDiscoveryFiltersSelector = createSelector(
  (state: IConnectSearchPage) => state.external.campaign,
  (state: IConnectSearchPage) => state.external.organization,
  (state: IConnectSearchPage) => state.external.clientId,
  (campaign, organization, clientId) => {
    const featureEnabled = organization && hasFeature(organization, 'creator_discovery');
    return (
      !isNil(clientId) || (
        featureEnabled &&
        campaign &&
        size(campaign.network_types) === 1 &&
        first(campaign.network_types) === 'instagram' &&
        campaign.campaign_goal === 'content_creation'
      )
    );
  },
);

const networkTypeSelector = (state: IConnectSearchPage) => {
  return state.textSearch.network ? state.textSearch.network.channel : null;
};

export const isQaSelector = (state: IConnectSearchPage) => state.external.isQa;

export const showAdvancedFiltersSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return includes(['instagram', 'youtube', 'pinterest', 'tiktok'], networkType);
  },
);

export const showLocationFilterSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return includes(['instagram', 'facebook', 'twitter', 'youtube', 'pinterest', 'tiktok'], networkType);
  },
);

export const showCountryFilterSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return includes(['instagram', 'facebook', 'twitter', 'youtube', 'pinterest', 'tiktok'], networkType);
  },
);

export const showLanguageFilterSelector = createSelector(
  (state: IConnectSearchPage) => state.external.organization,
  (state: IConnectSearchPage) => state.external.clientId,
  (organization, clientId) => {
    return !isNil(organization) || !isNil(clientId);
  },
);

export const showFullAudienceFiltersSelector = createSelector(
  (state: IConnectSearchPage) => state.external.organization,
  (state: IConnectSearchPage) => state.external.clientId,
  networkTypeSelector,
  isQaSelector,
  (organization, clientId, networkType, isQa) => {
    return (
      includes(['instagram', 'pinterest', 'tiktok'], networkType) &&
      (isQa || hasFeature(organization, 'audience_demographics') || !isNil(clientId))
    );
  },
);

export const showCategorizedFiltersSelector = createSelector(
  (state: IConnectSearchPage) => state.external.organization,
  (state: IConnectSearchPage) => state.external.clientId,
  networkTypeSelector,
  isQaSelector,
  (organization, clientId, networkType, isQa) => {
    return (
      networkType !== 'pinterest' &&
      (isQa || hasFeature(organization, 'categorized_search') || !isNil(clientId))
    );
  },
);

export const showLastActivityFiltersSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType !== 'pinterest';
  },
);

export const showAudienceDemographicsFiltersSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType !== 'pinterest';
  },
);

export const showAudienceAgeFiltersSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType !== 'pinterest';
  },
);

export const showInterestsFilterSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType === 'instagram';
  },
);

export const showAudienceEthnicitySelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType === 'instagram';
  },
);

export const showAudienceGenderSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType === 'instagram' || networkType === 'tiktok';
  },
);

export const showEngagementFilterSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType !== 'tiktok';
  },
);

export const showImpressionFilterSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType === 'tiktok';
  },
);

export const showEngagementRatioFilterSelector = createSelector(
  networkTypeSelector,
  (networkType) => {
    return networkType !== 'pinterest' && networkType !== 'blog';
  }
);

const roundToNearestStep = (min: number, value: number, step: number) => {
  if (value) {
    const diff = value - min;
    return min + (diff - (diff % step));
  }
  return value;
};

export const textSearchOptionsSelector = createSelector(
  (state: IConnectSearchPage) => state.external.campaign,
  (state: IConnectSearchPage) => state.external.clientId,
  (state: IConnectSearchPage) => state.external.searchOptions,
  (state: IConnectSearchPage) => [
    showCreatorDiscoveryFiltersSelector(state),
    showAdvancedFiltersSelector(state),
    showLocationFilterSelector(state),
    showCountryFilterSelector(state),
    showLanguageFilterSelector(state),
    showFullAudienceFiltersSelector(state),
    showCategorizedFiltersSelector(state),
    showLastActivityFiltersSelector(state),
    showAudienceDemographicsFiltersSelector(state),
    showAudienceAgeFiltersSelector(state),
    showEngagementFilterSelector(state),
    showImpressionFilterSelector(state),
    showInterestsFilterSelector(state),
    showAudienceEthnicitySelector(state),
    showAudienceGenderSelector(state),
    showEngagementRatioFilterSelector(state),
  ],
  isQaSelector,

  (state: IConnectSearchPage) => state.external.defaultEngagementLimitsByNetwork,
  (state: IConnectSearchPage) => state.external.defaultReachLimitsByNetwork,
  (state: IConnectSearchPage) => state.external.defaultImpressionLimitsByNetwork,
  (
    campaign,
    clientId,
    searchOptions,
    showFilters,
    isQa,
    defaultEngagmentLimitsByNetwork,
    defaultReachLimitsByNetwork,
    defaultImpressionLimitsByNetwork,
  ): ICreatorSearchOptions => {
    const [
      showCreatorDiscoveryFilters,
      showAdvancedFilters,
      showLocationFilter,
      showCountryFilter,
      showLanguageFilter,
      showFullAudienceFilters,
      showCategorizedFilters,
      showLastActivityFilters,
      showAudienceDemographicsFilters,
      showAudienceAgeFilter,
      showEngagementFilter,
      showImpressionFilter,
      showInterestsFilter,
      showAudienceEthnicityFilter,
      showAudienceGenderFilter,
      showEngagementRatioFilter,
    ]: boolean[] = showFilters;
    const engagementLimitsByNetwork = campaign ? { ...campaign.engagement_ranges } : defaultEngagmentLimitsByNetwork;
    const reachLimitsByNetwork = campaign ? { ...campaign.follower_ranges } : defaultReachLimitsByNetwork;
    const impressionLimitsByNetwork = campaign ? { ...campaign.impression_ranges } : defaultImpressionLimitsByNetwork;

    const rangeStep = 500;
    const impressionStep = 100;

    each(engagementLimitsByNetwork, (limits) => {
      limits[1] = roundToNearestStep(limits[0], limits[1], rangeStep);
    });

    each(reachLimitsByNetwork, (limits) => {
      limits[1] = roundToNearestStep(limits[0], limits[1], rangeStep);
    });

    each(impressionLimitsByNetwork, (limits) => {
      limits[1] = roundToNearestStep(limits[0], limits[1], impressionStep);
    });

    const options: ICreatorSearchOptions = {
      creatorSkillsets: {
        hide: !showCreatorDiscoveryFilters,
      },
      network: {
        hideHasSponsoredPosts: !showCreatorDiscoveryFilters,
      },
      creatorDemographics: {
        hide: !showAdvancedFilters,
        hideCreatorAge: !showFullAudienceFilters || !showAudienceAgeFilter,
        hideCreatorLocations: !showLocationFilter,
        hideCreatorCountries: !showCountryFilter,
        hideCreatorLanguages: !showLanguageFilter,
        hideCreatorInterests: !showInterestsFilter,
      },
      audienceDemographics: {
        hide: !showAdvancedFilters || !showAudienceDemographicsFilters,
        hideAudienceAge: !showFullAudienceFilters || !showAudienceAgeFilter,
        hideAudienceLocation: !showFullAudienceFilters,
        hideAudiencePercentage: !showFullAudienceFilters,
        hideAudienceEthnicity: !showAudienceEthnicityFilter,
        hideAudienceGender: !showAudienceGenderFilter,
      },
      engagement: {
        reachLimitsByNetwork,
        reachStep: rangeStep,
        engagementLimitsByNetwork,
        engagementStep: rangeStep,
        hideEngagement: !showEngagementFilter,
        impressionLimitsByNetwork,
        impressionStep,
        hideImpression: !showImpressionFilter,
        hideEngagementRatio: !showEngagementRatioFilter,
      },
      other: {
        hideInvitedToggle: !isNil(clientId),
        hideTags: !showCategorizedFilters,
        hideCanContact: !isQa,
        hideActivity: !showLastActivityFilters
      },
    };

    return merge(options, searchOptions);
  },
);

export const imageSearchAPIParamsSelector = createSelector(
  (state: IConnectSearchPage) => state.external.campaign,
  (state: IConnectSearchPage) => state.imageSearch,
  textSearchOptionsSelector,
  (state: IConnectSearchPage) => state.searchResults.resultsCount,
  (state: IConnectSearchPage) => state.external.clientId,
  (campaign, imageSearch, options, totalCount, clientId) => ({
    ...mapSearchFiltersToAPIParams(
      {
        network: {
          channel: 'instagram',
        },
        creatorDemographics: {
          countries: imageSearch.countries,
        },
        other: {
          hideInvited: imageSearch.hideInvited,
        },
      },
      options,
    ),
    campaign_id: campaign && campaign.id,
    image_url: imageSearch.imageURL,
    totalCount,
    client_id: clientId
  }),
);

export const defaultImageSearchAPIParamsSelector = createSelector(
  (state: IConnectSearchPage) => state,
  (state: IConnectSearchPage) => state.external.defaultImageSearchUrl,
  (state, defaultImageSearchUrl) => ({
    ...imageSearchAPIParamsSelector(state),
    image_url: defaultImageSearchUrl,
  }),
);

const filterHiddenParametersSelector = createSelector(
  showLocationFilterSelector,
  showCountryFilterSelector,
  showLanguageFilterSelector,
  (showLocationFilter, showCountryFilter, showLanguageFilter) => {
    return (params) => {
      const copy = { ...params };

      if (!showLocationFilter) {
        delete copy.locations;
      }

      if (!showCountryFilter) {
        delete params.country_codes;
      }

      if (!showLanguageFilter) {
        delete params.language_codes;
      }

      return params;
    };
  },
);

export const searchAPIParamsSelector = createSelector(
  (state: IConnectSearchPage) => state.external.campaign,
  (state: IConnectSearchPage) => state.textSearch,
  textSearchOptionsSelector,
  (state: IConnectSearchPage) => state.searchResults.resultsCount,
  (state: IConnectSearchPage) => state.external.clientId,
  filterHiddenParametersSelector,
  (campaign, filters, options, totalCount, clientId, filterHiddenParameters) =>
    filterHiddenParameters({
      ...mapSearchFiltersToAPIParams(filters, options),
      campaign_id: campaign && campaign.id,
      totalCount,
      client_id: clientId
    }),
);
